import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/fire';
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes, FaTag } from 'react-icons/fa';

export default function CategoryManagement({ isSecAdmin, theme = 'light', onCategoriesChange }) {
    const [categories, setCategories] = useState([]);
    const [newName, setNewName] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => { fetchCategories(); }, []);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, 'categories'));
            setCategories(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => a.name.localeCompare(b.name)));
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        const name = newName.trim();
        if (!name) return;
        if (categories.some(c => c.name.toLowerCase() === name.toLowerCase())) {
            alert('Category already exists');
            return;
        }
        await addDoc(collection(db, 'categories'), { name });
        setNewName('');
        await fetchCategories();
        onCategoriesChange?.();
    };

    const handleSaveEdit = async (id) => {
        const name = editingName.trim();
        if (!name) return;
        await updateDoc(doc(db, 'categories', id), { name });
        setEditingId(null);
        await fetchCategories();
        onCategoriesChange?.();
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this category? Members and coaches assigned to it will need to be reassigned.')) return;
        await deleteDoc(doc(db, 'categories', id));
        await fetchCategories();
        onCategoriesChange?.();
    };

    const isDark = theme === 'dark';

    return (
        <div className="p-4 sm:p-6 max-w-xl">
            <h2 className={`text-xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                Manage Categories
            </h2>
            <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Categories group members and coaches together for the Class Performance Report (e.g. BJJ, MMA, Kids, Kickboxing).
            </p>

            {!isSecAdmin && (
                <div className="flex gap-2 mb-6">
                    <input
                        type="text"
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAdd()}
                        placeholder="New category (e.g. BJJ, MMA, Kids)"
                        className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                            isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    />
                    <button
                        onClick={handleAdd}
                        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium whitespace-nowrap"
                    >
                        <FaPlus /> Add
                    </button>
                </div>
            )}

            {loading && (
                <p className={`text-sm italic ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</p>
            )}

            <div className="space-y-2">
                {!loading && categories.length === 0 && (
                    <p className={`text-sm italic ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        No categories yet. Add one above.
                    </p>
                )}
                {categories.map(cat => (
                    <div
                        key={cat.id}
                        className={`flex items-center justify-between px-4 py-3 rounded-lg border ${
                            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'
                        }`}
                    >
                        {editingId === cat.id ? (
                            <>
                                <input
                                    type="text"
                                    value={editingName}
                                    onChange={e => setEditingName(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(cat.id); if (e.key === 'Escape') setEditingId(null); }}
                                    autoFocus
                                    className={`flex-1 mr-3 px-2 py-1 border rounded text-sm ${
                                        isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                                    }`}
                                />
                                <div className="flex gap-3">
                                    <button onClick={() => handleSaveEdit(cat.id)} className="text-green-500 hover:text-green-700" title="Save"><FaCheck /></button>
                                    <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600" title="Cancel"><FaTimes /></button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="flex items-center gap-2">
                                    <FaTag className={isDark ? 'text-blue-400' : 'text-blue-500'} size={12} />
                                    <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>{cat.name}</span>
                                </div>
                                {!isSecAdmin && (
                                    <div className="flex gap-4">
                                        <button
                                            onClick={() => { setEditingId(cat.id); setEditingName(cat.name); }}
                                            className="text-blue-500 hover:text-blue-700"
                                            title="Edit"
                                        >
                                            <FaEdit size={13} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(cat.id)}
                                            className="text-red-500 hover:text-red-700"
                                            title="Delete"
                                        >
                                            <FaTrash size={13} />
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
